import { Flame, Snowflake, Thermometer, type LucideIcon } from "lucide-react";
import { useDataProvider, useNotify, useRefresh, useTranslate } from "ra-core";
import { useState } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

import type { NevoraDataProvider } from "../datos/dataProvider";
import { conInteres, NIVELES_INTERES } from "../dominio/catalogos";
import type { Negocio, NivelInteres } from "../dominio/tipos";

/**
 * Nivel de interés frío / tibio / caliente (28d, Robert 15/10/2026): se elige al registrar
 * «Ni sí ni no», se cambia con un toque desde la tarjeta y la ficha y ordena el Pipeline. Sin la
 * 28d en la base la ficha no trae `interes` y no se enseña nada.
 */
const ICONO: Record<NivelInteres, LucideIcon> = {
  caliente: Flame,
  tibio: Thermometer,
  frio: Snowflake,
};

const COLOR: Record<NivelInteres, string> = {
  caliente:
    "border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300",
  tibio:
    "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300",
  frio: "border-sky-300 bg-sky-50 text-sky-700 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-300",
};

/** Tres botones (hoja de resultado). */
export const BotonesInteres = ({
  valor,
  cambiar,
}: {
  valor: NivelInteres | null;
  cambiar: (n: NivelInteres) => void;
}) => {
  const translate = useTranslate();
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="botones-interes">
      {NIVELES_INTERES.map((n) => {
        const Icono = ICONO[n];
        const activo = valor === n;
        return (
          <button
            key={n}
            type="button"
            onClick={() => cambiar(n)}
            aria-pressed={activo}
            className={cn(
              "flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-sm font-medium transition-colors",
              activo ? COLOR[n] : "border-border bg-card hover:bg-muted",
            )}
          >
            <Icono className="size-4" aria-hidden />
            {translate(`nevora.interes.${n}`)}
          </button>
        );
      })}
    </div>
  );
};

/**
 * El nivel con un toque (tarjeta del Pipeline y ficha): un botón con su icono que abre la
 * lista. No abre la ficha ni empieza un arrastre.
 */
export const MenuInteres = ({
  negocio,
  puedeCambiar,
  conTexto = false,
  soloIcono = false,
}: {
  negocio: Negocio;
  puedeCambiar: boolean;
  conTexto?: boolean;
  /** Solo el icono aunque haya nivel (tarjeta del Pipeline); el nivel, al pasar el ratón. */
  soloIcono?: boolean;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const [guardando, setGuardando] = useState(false);
  if (!conInteres(negocio)) return null;
  const nivel = negocio.interes ?? null;
  const Icono = nivel ? ICONO[nivel] : Thermometer;
  const etiqueta = translate(
    nivel ? `nevora.interes.${nivel}` : "nevora.interes.sin_nivel",
  );
  const elegir = async (n: NivelInteres | null) => {
    if (n === nivel) return;
    setGuardando(true);
    try {
      await dataProvider.cambiarInteres(negocio.proyecto_id, n);
      notify("nevora.interes.guardado", { type: "success" });
      refresh();
    } catch {
      notify("nevora.interes.error", { type: "error" });
    } finally {
      setGuardando(false);
    }
  };
  const boton = (
    <button
      type="button"
      disabled={!puedeCambiar || guardando}
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11px] font-medium",
        nivel
          ? COLOR[nivel]
          : "border-dashed border-border text-muted-foreground",
        puedeCambiar ? "hover:opacity-80" : "cursor-default",
      )}
      title={translate("nevora.interes.cambiar", { nivel: etiqueta })}
      aria-label={translate("nevora.interes.cambiar", { nivel: etiqueta })}
      data-testid="nivel-interes"
      data-nivel={nivel ?? ""}
    >
      <Icono className="size-3.5" aria-hidden />
      {conTexto || (nivel && !soloIcono) ? <span>{etiqueta}</span> : null}
    </button>
  );
  // Los clics dentro (también los de la lista, que va en un portal) no llegan a la tarjeta.
  return (
    <span onClick={(e) => e.stopPropagation()} className="inline-flex">
      {puedeCambiar ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>{boton}</DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {NIVELES_INTERES.map((n) => {
              const I = ICONO[n];
              return (
                <DropdownMenuItem key={n} onSelect={() => elegir(n)}>
                  <I className="size-4" aria-hidden />
                  {translate(`nevora.interes.${n}`)}
                </DropdownMenuItem>
              );
            })}
            {nivel ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => elegir(null)}>
                  {translate("nevora.interes.quitar")}
                </DropdownMenuItem>
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        boton
      )}
    </span>
  );
};
